import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { PopupStyles } from "./CustomerDetailsStyles";
import ArtifactService from "./ArtifactService";
import InsuranceService from "./InsuranceService";
import AppointmentService from "./AppointmentService";
import WarrantyService from "./WarrantyService";
import { CUSTOMER_DIALOG, FETCH_ARTIFACTS } from "../../../../graphql/query/query";
import { useQuery } from "@apollo/client";
import { useAuth } from "../../../../context/Auth.context";
import { formatToDateOnly } from "../../../../utils/Utils";

const todayDate = formatToDateOnly(new Date());

const CustomerDetailTabContent = ({
  customer,
  tabValue: controlledTab,
  onTabChange,
  printMode = false,
}) => {
  const [internalTab, setInternalTab] = useState(1);
  const isControlled = controlledTab != null;
  const value = isControlled ? controlledTab : internalTab;
  const setValue = (next) => {
    if (isControlled) onTabChange?.(next);
    else setInternalTab(next);
  };

  const [insuranceService, setInsuranceService] = useState();
  const [insuranceDate, setInsuranceDate] = useState([{ date: todayDate }]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const [artifactService, setArtifactService] = useState({
    createdAt: "",
    surveyResponse: [],
    videoResponse: [],
    reviewResponse: [],
    referralResponse: [],
    appointmentResponse: [],
    vehicleTransactionResponse: null,
    appointment_preference: 3,
     serviceMaintenanceResponse: null,
  });
  const [artifactDate, setArtifactDate] = useState([todayDate]);

  const auth = useAuth();

  // ── Main dialog data ──────────────────────────────────────────────────────
  const { data: CustomerDetails, loading } = useQuery(CUSTOMER_DIALOG, {
    variables: {
      dialogInput: {
        user_id:          customer.customer.user_id,
        dealership_id:    auth.user.dealership_id,
        employee_user_id: customer?.employee?.employee_user_id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // ── Fetch artifact config to get vehicle/transaction question text ─────────
  const { data: artifactsData } = useQuery(FETCH_ARTIFACTS, {
    variables: { fetchInput: { dealershipId: auth.user.dealership_id } },
    fetchPolicy: "cache-first",
  });

  // First artifact in the array holds the question text for this dealership
  const vehicleArtifact =
    artifactsData?.fetchArtifacts?.vehicleTransactionArtifact?.[0] ?? null;

  // ── Dialog field aliases ──────────────────────────────────────────────────
  const getInsuranceData      = CustomerDetails?.customerDialog?.insuranceService;
  const quoteData             = CustomerDetails?.customerDialog?.quoteQuestions;
  const getVehicles           = CustomerDetails?.customerDialog?.vehicle;
  const getSurvey             = CustomerDetails?.customerDialog?.surveyResponse;
  const getVideo              = CustomerDetails?.customerDialog?.videoResponse;
  const getReview             = CustomerDetails?.customerDialog?.reviewResponse;
  const getReferral           = CustomerDetails?.customerDialog?.referralResponse;
  // const getAppointment        = CustomerDetails?.customerDialog?.appointmentResponse;
  const getAppointmentPreference = CustomerDetails?.customerDialog?.appointment_preference;

  const getVehicleAppointments =
    CustomerDetails?.customerDialog?.vehicleAppointments;
  // vehicleTransactionResponse has no created_at — never filter by date
  const getVehicleTransaction =
    CustomerDetails?.customerDialog?.vehicleTransactionResponse ?? null;

  useEffect(() => {
    const quotes = quoteData || [];
    const rawVehicles = (getVehicles || []).filter(
      (v) => Number(v.vehicle_status_id) === 1,
    );

    if (
      getInsuranceData?.length > 0 ||
      quotes.length > 0 ||
      rawVehicles.length > 0 ||
      getVehicleTransaction
    ) {
      const insuranceBase = (getInsuranceData || []).map((data) => ({
        date: data?.created_at,
        insurance_id: data?.insurance_id,
        object: {
          ...data,
          vehicle: [...(data?.vehicle || [])],
        },
      }));

      const insuranceByDate = new Map();
      insuranceBase.forEach((entry) => {
        if (!entry.date) return;
        if (!insuranceByDate.has(entry.date)) insuranceByDate.set(entry.date, []);
        insuranceByDate.get(entry.date).push(entry);
      });

      const leftoverVehicles = [];
      rawVehicles.forEach((vehicle) => {
        const sameDayVisits = insuranceByDate.get(vehicle.created_at);
        if (sameDayVisits?.length) {
          const target = sameDayVisits[0].object;
          const alreadyListed = target.vehicle.some(
            (row) =>
              row.vehicle_id === vehicle.vehicle_id ||
              (String(row.make_year ?? "") === String(vehicle.make_year ?? "") &&
                (row.make ?? "").toLowerCase() ===
                  (vehicle.make ?? "").toLowerCase() &&
                (row.model ?? "").toLowerCase() ===
                  (vehicle.model ?? "").toLowerCase()),
          );
          if (!alreadyListed) target.vehicle.push(vehicle);
          return;
        }
        leftoverVehicles.push(vehicle);
      });

      const leftoverQuotes = quotes.filter(
        (quote) => !insuranceByDate.has(quote?.created_at),
      );

      let combinedInsurance = [...insuranceBase];

      const standaloneDates = [
        ...new Set(
          [...leftoverQuotes, ...leftoverVehicles]
            .map((item) => item?.created_at)
            .filter(Boolean),
        ),
      ].sort((a, b) => new Date(b) - new Date(a));

      standaloneDates.forEach((date) => {
        combinedInsurance.push({
          date,
          insurance_id: null,
          object: [
            ...leftoverQuotes.filter((q) => q?.created_at === date),
            ...leftoverVehicles.filter((v) => v.created_at === date),
          ],
        });
      });

      if (combinedInsurance.length === 0 && getVehicleTransaction) {
        combinedInsurance.push({
          date: todayDate,
          insurance_id: null,
          object: [],
        });
      }

      combinedInsurance.sort((a, b) => new Date(b.date) - new Date(a.date));
      setInsuranceDate(combinedInsurance);

      if (combinedInsurance.length > 0) {
        setInsuranceService(combinedInsurance[0]);
        setSelectedIndex(0);
      }
    } else if (CustomerDetails?.customerDialog) {
      setInsuranceDate([]);
      setInsuranceService(undefined);
    }

    if (getSurvey || getVideo || getReview || getReferral) {
      let array = [
        ...getSurvey,
        ...getVideo,
        ...getReview,
        ...getReferral,
        // ...getAppointment,
      ];

      let createdAt = [...new Set(array.map((item) => item?.created_at))]
        .filter(Boolean)
        .sort((a, b) => new Date(b) - new Date(a));

      if (createdAt.length === 0) createdAt = [todayDate];
      setArtifactDate(createdAt);

      const selectResponses = (type, date) =>
        type?.filter((x) => x.created_at === date) || [];

      setArtifactService({
        createdAt:          createdAt[0],
        surveyResponse:     selectResponses(getSurvey,      createdAt[0]),
        videoResponse:      selectResponses(getVideo,       createdAt[0]),
        reviewResponse:     selectResponses(getReview,      createdAt[0]),
        referralResponse:   selectResponses(getReferral,    createdAt[0]),

        vehicleTransactionResponse: getVehicleTransaction,
        appointment_preference:     getAppointmentPreference,
      
      });

      if (value === 2) setSelectedIndex(0);
    } else {
      setArtifactDate([todayDate]);
    }
  }, [CustomerDetails]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [value]);

  const handleChange = (_event, newValue) => {
    setValue(newValue);
    setSelectedIndex(-1);
  };

  const handleInsurance = (data, index) => {
    setInsuranceService(data);
    setSelectedIndex(index);
  };

  const selectedInsurance = insuranceService || insuranceDate?.[0];
  const selectedVisitVehicles = selectedInsurance?.insurance_id
    ? selectedInsurance?.object?.vehicle || []
    : Array.isArray(selectedInsurance?.object)
      ? selectedInsurance.object.filter((item) => item?.vehicle_id)
      : [];
  const selectedHasNewVehicle = selectedVisitVehicles.some(
    (vehicle) => Number(vehicle.vehicle_status_id) === 1,
  );

  const handleArtifact = (date) => {
    const selectResponses = (type) =>
      type?.filter((x) => x.created_at === date) || [];

    setArtifactService({
      createdAt:          date,
      surveyResponse:     selectResponses(getSurvey),
      videoResponse:      selectResponses(getVideo),
      reviewResponse:     selectResponses(getReview),
      referralResponse:   selectResponses(getReferral),
      // appointmentResponse: selectResponses(getAppointment),
      vehicleTransactionResponse: getVehicleTransaction,
      appointment_preference:     getAppointmentPreference,

    });
  };

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <Box
        sx={{
          display:        "flex",
          flexDirection:  "row",
          justifyContent: "space-between",
          alignItems:     "center",
          mb: 2,
        }}
      >
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Insurance" value={1} />
          <Tab label="Artifacts" value={2} />
          <Tab label="Appointments" value={3} />
          <Tab label="Warranty" value={4} />
        </Tabs>
        {(value === 1 || value === 2) && (
          <Box>
            <FormControl sx={{ m: 1 }}>
              <Select
                value={selectedIndex}
                size="small"
                style={{ fontSize: "14px", width: "200px" }}
                displayEmpty
              >
                {value === 1 &&
                  insuranceDate?.map((data, index) => (
                    <MenuItem
                      key={index}
                      value={index}
                      onClick={() => handleInsurance(data, index)}
                    >
                      {data?.date || todayDate}
                    </MenuItem>
                  ))}
                {value === 2 &&
                  artifactDate?.map((date, index) => (
                    <MenuItem
                      key={index}
                      value={index}
                      onClick={() => {
                        handleArtifact(date);
                        setSelectedIndex(index);
                      }}
                    >
                      {date || todayDate}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {CustomerDetails?.customerDialog ? (
        <>
          {value === 1 && insuranceDate?.length > 0 ? (
            <InsuranceService
              insuranceDetails={selectedInsurance}
              vehicleTransactionResponse={
                selectedHasNewVehicle ? null : getVehicleTransaction
              }
            />
          ) : value === 2 && artifactDate.length > 0 ? (
            // ✅ Pass vehicleArtifact so ArtifactService can use the real question text
            <ArtifactService
              artifactService={artifactService}
              vehicleArtifact={vehicleArtifact}
            />
          ) : value === 3 ? (
            <AppointmentService
              vehicleAppointments={getVehicleAppointments}
              appointmentPreference={getAppointmentPreference}
            />
          ) : value === 4 ? (
            <WarrantyService customer={customer} />
          ) : (
            <Box
              sx={{
                display:        "flex",
                justifyContent: "center",
                alignItems:     "center",
                height:         "100%",
              }}
            >
              <Typography>No Data Available</Typography>
            </Box>
          )}
        </>
      ) : loading ? (
        <Box sx={PopupStyles.loadingStyle}>
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <Box sx={PopupStyles.loadingStyle}>
          <Typography>Details not found</Typography>
        </Box>
      )}
    </Box>
  );
};

export default CustomerDetailTabContent;